import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faPlay,
  faUndo,
  faVolume,
  faVolumeMute,
} from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/Button';
import SelectField, {
  type SelectFieldOptionObject,
} from 'components/v2/SelectField/SelectField';
import { isSafari } from 'shared/helpers/isSafari';
import useFrameStepNavigation from './hooks/useFrameStepNavigation';

export enum VideoPlayState {
  initial = 'initial',
  playing = 'playing',
  paused = 'paused',
  ended = 'ended',
}

export type VideoPlayerControlsConfig = {
  showPlayPause: boolean;
  showTimeline: boolean;
  showDuration: boolean;
  showPlaybackRate: boolean;
  showMute: boolean;
};

export type VideoPlayerControlsContext = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  isDisabled: boolean;
  playState: VideoPlayState;
  currentTime: number;
  duration: number;
  isMuted: boolean;
  setCurrentTime: (time: number) => void;
};

export type VideoPlayerExtraControls =
  | React.ReactNode
  | ((context: VideoPlayerControlsContext) => React.ReactNode);

type VideoPlayerControlsProps = {
  playState: VideoPlayState;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  duration: number;
  isMuted: boolean;
  mute: () => void;
  unmute: () => void;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  controlsConfig: VideoPlayerControlsConfig;
  isDisabled?: boolean;
  isLive?: boolean;
  enableKeyboardShortcuts?: boolean;
  enableFrameStepShortcuts?: boolean;
  extraControls?: VideoPlayerExtraControls;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
};

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return '00:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
};

const formatOption = (value: number): SelectFieldOptionObject => ({
  value: `${value}`,
  label: `${value}x`,
});

const DEFAULT_PLAYBACK_RATE_VALUE = 1;
const PLAYBACK_RATE_VALUES = isSafari()
  ? [0.5, DEFAULT_PLAYBACK_RATE_VALUE, 1.5, 2]
  : [0.5, DEFAULT_PLAYBACK_RATE_VALUE, 1.5, 2, 4, 8];
const defaultPlaybackRateOption = formatOption(DEFAULT_PLAYBACK_RATE_VALUE);
const playbackRateOptions = PLAYBACK_RATE_VALUES.map(value =>
  formatOption(value)
);

const FRAME_RATE = 1 / 30;

const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
  playState,
  currentTime,
  setCurrentTime,
  duration,
  isMuted,
  mute,
  unmute,
  onPlay,
  onPause,
  onReplay,
  controlsConfig,
  isDisabled = false,
  isLive = false,
  enableKeyboardShortcuts = false,
  enableFrameStepShortcuts = false,
  extraControls,
  videoRef,
}) => {
  const [playbackRate, setPlaybackRate] = useState<SelectFieldOptionObject>(
    defaultPlaybackRateOption
  );
  const wasVideoPlayingWhenTimelineDragged = useRef(false);

  useFrameStepNavigation(videoRef, enableFrameStepShortcuts);

  const mainAction = useMemo(
    () => ({
      initial: { icon: faPlay, title: 'Play', onClick: onPlay },
      playing: { icon: faPause, title: 'Pause', onClick: onPause },
      paused: { icon: faPlay, title: 'Play', onClick: onPlay },
      ended: { icon: faUndo, title: 'Replay', onClick: onReplay },
    }),
    [onPause, onPlay, onReplay]
  );

  const muteAction = useMemo(
    () => ({
      unmuted: { icon: faVolume, title: 'Mute', onClick: mute },
      muted: { icon: faVolumeMute, title: 'Unmute', onClick: unmute },
    }),
    [mute, unmute]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enableKeyboardShortcuts) return;
      const { activeElement } = document;
      const isFocusOnInteractiveElement =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'BUTTON' ||
        activeElement?.className.includes('modal');

      if (isDisabled) return;
      if (isFocusOnInteractiveElement) return;

      switch (event.key.toLowerCase()) {
        case ' ':
          event.preventDefault();
          if (playState === VideoPlayState.playing) {
            onPause();
          } else {
            onPlay();
          }
          break;
        case 'm':
          event.preventDefault();
          if (isMuted) {
            unmute();
          } else {
            mute();
          }
          break;
        case 'j':
          event.preventDefault();
          {
            const currentPlaybackRate = Number(playbackRate.value);
            const optionIndex = playbackRateOptions.findIndex(
              option => option.value === String(currentPlaybackRate)
            );
            setPlaybackRate(
              playbackRateOptions[optionIndex - 1] || playbackRate
            );
          }
          break;
        case 'l':
          event.preventDefault();
          {
            const currentPlaybackRate = Number(playbackRate.value);
            const optionIndex = playbackRateOptions.findIndex(
              option => option.value === String(currentPlaybackRate)
            );
            setPlaybackRate(
              playbackRateOptions[optionIndex + 1] || playbackRate
            );
          }
          break;
        default:
          break;
      }
    },
    [
      playState,
      enableKeyboardShortcuts,
      isDisabled,
      isMuted,
      mute,
      onPause,
      onPlay,
      playbackRate,
      setPlaybackRate,
      unmute,
    ]
  );

  useEffect(() => {
    if (!enableKeyboardShortcuts) return undefined;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableKeyboardShortcuts, handleKeyDown]);

  useEffect(() => {
    if (videoRef.current && playbackRate) {
      const currentVideoRef = videoRef.current;
      currentVideoRef.playbackRate = Number(playbackRate.value);
    }
  }, [playbackRate, videoRef]);

  const onMouseDown = useCallback(() => {
    wasVideoPlayingWhenTimelineDragged.current =
      playState === VideoPlayState.playing;
    if (wasVideoPlayingWhenTimelineDragged.current) {
      onPause();
    }
  }, [onPause, playState]);

  const onMouseUp = useCallback(() => {
    if (wasVideoPlayingWhenTimelineDragged.current) {
      setTimeout(() => {
        onPlay();
      }, 50);
    }
  }, [onPlay]);

  const handlePlaybackRateChange = useCallback(
    (rate: SelectFieldOptionObject | null) => {
      if (!rate) return;
      setPlaybackRate(rate);
    },
    []
  );

  const controlsContext = useMemo(
    () => ({
      videoRef,
      isDisabled,
      playState,
      currentTime,
      duration,
      isMuted,
      setCurrentTime,
    }),
    [
      currentTime,
      duration,
      isDisabled,
      isMuted,
      playState,
      setCurrentTime,
      videoRef,
    ]
  );

  const renderedExtraControls =
    typeof extraControls === 'function'
      ? extraControls(controlsContext)
      : extraControls;

  return (
    <figcaption
      className={cx('video-player-controls', {
        isDisabled,
      })}
    >
      {!isLive ? (
        controlsConfig.showTimeline && (
          <input
            disabled={isDisabled || duration <= 0}
            type="range"
            min="0"
            max={duration}
            step={FRAME_RATE}
            value={Math.min(currentTime, duration)}
            onChange={event => setCurrentTime(event.target.valueAsNumber)}
            className="timestamp-picker-progress"
            style={
              {
                '--progress':
                  duration > 0
                    ? `${(Math.min(currentTime, duration) * 100) / duration}%`
                    : '0%',
              } as React.CSSProperties
            }
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            aria-label="Seek"
          />
        )
      ) : (
        <div className="video-player-controls__live-progress" />
      )}

      <div className="timestamp-picker-control-actions">
        <div className="timestamp-picker-control-actions-wrapper">
          <div className="timestamp-picker-control-actions-block left">
            {controlsConfig.showPlayPause && (
              <Button
                className="timestamp-picker-control"
                color="info"
                icon={
                  <FontAwesomeIcon
                    icon={mainAction[playState].icon}
                    size="sm"
                  />
                }
                onClick={mainAction[playState].onClick}
                title={mainAction[playState].title}
                aria-label={mainAction[playState].title}
                disabled={isDisabled}
              />
            )}
            {!isLive &&
              controlsConfig.showDuration &&
              duration > 0 &&
              controlsConfig.showTimeline && (
                <p className="time-indicator">
                  <time>{formatTime(currentTime)}</time>
                  <span>|</span>
                  <time>{formatTime(duration)}</time>
                </p>
              )}
          </div>

          <div className="timestamp-picker-control-actions-block right">
            {!isLive && controlsConfig.showPlaybackRate && (
              <SelectField
                controlledValue={playbackRate}
                className="clip-player-playback-rate-select"
                options={playbackRateOptions}
                onChange={handlePlaybackRateChange}
                isClearable={false}
                isSearchable={false}
                isMulti={false}
                isDisabled={!videoRef.current || isDisabled}
                readOnly={isDisabled}
              />
            )}
            {controlsConfig.showMute && (
              <Button
                className="timestamp-picker-control"
                color="info"
                icon={
                  <FontAwesomeIcon
                    icon={muteAction[isMuted ? 'muted' : 'unmuted'].icon}
                    size="sm"
                  />
                }
                onClick={muteAction[isMuted ? 'muted' : 'unmuted'].onClick}
                title={muteAction[isMuted ? 'muted' : 'unmuted'].title}
                aria-label={muteAction[isMuted ? 'muted' : 'unmuted'].title}
                disabled={isDisabled}
              />
            )}
            {renderedExtraControls}
          </div>
        </div>
      </div>
    </figcaption>
  );
};

export default VideoPlayerControls;
